import React, { useMemo, useState } from 'react'
import {
  PerformanceStatsTable,
  PerformanceStatsTableLoadingProps,
} from '../../../../components/stats/table/PerformanceStatsTable'
import { FormattedInventoryPerformanceStats, INVENTORY_DATA_TYPE } from '../../../../interfaces/stats'
import {
  mapTotalData,
  mapData,
  mapTimeSeriesData,
  mapPositionsAndWebsitesData,
} from '../../../../utils/performanceStatsDataMap'
import { useAdvertiserStatsColumns } from '../../../../utils/hooks/performanceStats/useStatsColumns'
import mapEnumForSelect from '../../../../utils/mapEnumForSelect'
import { useUserPermissions } from '../../../company/userPermissions'

interface InventoryPerformanceStatsTableProps extends PerformanceStatsTableLoadingProps {
  performanceStats: FormattedInventoryPerformanceStats | null
  dateFormat: string
  timezone: string
}

const InventoryPerformanceStatsTable: React.FC<InventoryPerformanceStatsTableProps> = ({
  performanceStats,
  dateFormat,
  timezone,
  error,
  loading,
}): JSX.Element => {
  const permissions = useUserPermissions()
  const allDataTypes = mapEnumForSelect(INVENTORY_DATA_TYPE).filter((type) =>
    type.value === 'resellers' ? permissions.superAdmin : true
  )
  const [selectedDataType, setSelectedDataType] = useState(INVENTORY_DATA_TYPE.TIMESERIES)

  const totalData = useMemo(() => mapTotalData(performanceStats?.stats), [performanceStats])
  const timeSeriesData = useMemo(() => mapTimeSeriesData(performanceStats?.timeseries), [performanceStats])
  const resellerData = useMemo(() => mapData(performanceStats?.resellers ?? []), [performanceStats])
  const positionsAndWebsitesData = useMemo(
    () => mapPositionsAndWebsitesData(performanceStats?.websites),
    [performanceStats]
  )

  const dataTypeMap = useMemo(
    () => ({
      [INVENTORY_DATA_TYPE.TIMESERIES]: timeSeriesData,
      [INVENTORY_DATA_TYPE.RESELLERS]: resellerData,
      [INVENTORY_DATA_TYPE.WEBSITES_AND_POSITIONS]: positionsAndWebsitesData,
    }),
    [selectedDataType, performanceStats]
  )

  const dataToDisplay = useMemo(() => dataTypeMap[selectedDataType], [dataTypeMap, selectedDataType])

  const columns = useAdvertiserStatsColumns(selectedDataType, totalData, {
    dateFormat,
    timezone,
    allDataTypes,
    selectedDataType,
    selectedDataTypeChange: setSelectedDataType,
  })
  return <PerformanceStatsTable columns={columns} toDisplay={dataToDisplay} error={error} loading={loading} />
}

export default InventoryPerformanceStatsTable
